import type { BasicBottleneckCandidate, BasicBottleneckEvidence, BottleneckNodeInput } from './contracts';

interface NormalizedSignal {
  nodeId: string;
  value: number | null;
}

/**
 * SPEC-009 DF-156/§76 — min-max normalizes a raw signal into a 0..1
 * "relative position" among comparable stages. No sourced benchmark exists
 * (project rule §23), so "relative" always means relative to the OTHER
 * stages in this same evaluation, never an external threshold.
 */
function normalize(raw: readonly { nodeId: string; value: number | null }[]): NormalizedSignal[] {
  const known = raw.filter((entry): entry is { nodeId: string; value: number } => entry.value !== null);
  if (known.length === 0) return raw.map((entry) => ({ nodeId: entry.nodeId, value: null }));
  const min = Math.min(...known.map((entry) => entry.value));
  const max = Math.max(...known.map((entry) => entry.value));
  const range = max - min;
  return raw.map((entry) => {
    if (entry.value === null) return { nodeId: entry.nodeId, value: null };
    return { nodeId: entry.nodeId, value: range > 0 ? (entry.value - min) / range : null };
  });
}

function mean(values: readonly number[]): number | null {
  if (values.length === 0) return null;
  return values.reduce((total, value) => total + value, 0) / values.length;
}

/**
 * SPEC-009 DF-156/§77-79 — builds ranked candidates. A missing signal is
 * excluded from the mean (never treated as zero); a candidate with zero
 * known signals is unranked (`rankScore: null`) rather than forced to last
 * with a fabricated score. Tie-break: rankScore desc, then original graph
 * order, then nodeId for full determinism.
 */
export function rankBottleneckCandidates(nodes: readonly BottleneckNodeInput[]): BasicBottleneckCandidate[] {
  // Conversion loss: higher loss = lower conversion, so invert before normalizing.
  const conversionLossRaw = nodes.map((node) => ({
    nodeId: node.nodeId,
    value: node.conversion === null ? null : -node.conversion,
  }));
  const conversionLoss = normalize(conversionLossRaw);

  const volumeLossRaw = nodes.map((node) => ({
    nodeId: node.nodeId,
    value: node.entries === null || node.exits === null ? null : Math.max(0, node.entries - node.exits),
  }));
  const volumeLoss = normalize(volumeLossRaw);

  const targetGapRaw = nodes.map((node) => ({
    nodeId: node.nodeId,
    value: node.targetGap == null ? null : Math.max(0, node.targetGap),
  }));
  const targetGap = normalize(targetGapRaw);

  const byId = <T extends { nodeId: string }>(list: readonly T[], id: string) =>
    list.find((entry) => entry.nodeId === id) ?? null;

  const candidates: BasicBottleneckCandidate[] = nodes.map((node) => {
    const conv = byId(conversionLoss, node.nodeId)?.value ?? null;
    const vol = byId(volumeLoss, node.nodeId)?.value ?? null;
    const gap = byId(targetGap, node.nodeId)?.value ?? null;
    const available = [conv, vol, gap].filter((value): value is number => value !== null);
    const evidence: BasicBottleneckEvidence[] = [];
    if (conv !== null) evidence.push({ signal: 'conversion', value: conv });
    if (vol !== null) evidence.push({ signal: 'volume', value: vol });
    if (gap !== null) evidence.push({ signal: 'target_gap', value: gap });
    return {
      nodeId: node.nodeId,
      stageRef: node.stageRef,
      relativeConversionLoss: conv,
      relativeVolumeLoss: vol,
      targetGap: gap,
      rankScore: mean(available),
      evidence,
      warnings: [],
    };
  });

  return candidates
    .map((candidate, index) => ({ candidate, index }))
    .sort((a, b) => {
      if (a.candidate.rankScore === null && b.candidate.rankScore === null) {
        return a.index - b.index || a.candidate.nodeId.localeCompare(b.candidate.nodeId);
      }
      if (a.candidate.rankScore === null) return 1;
      if (b.candidate.rankScore === null) return -1;
      if (b.candidate.rankScore !== a.candidate.rankScore) return b.candidate.rankScore - a.candidate.rankScore;
      return a.index - b.index || a.candidate.nodeId.localeCompare(b.candidate.nodeId);
    })
    .map((entry) => entry.candidate);
}
